import { useState, useCallback, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TapToHearProps {
  text: string;
  language?: string;
  className?: string;
}

export const TapToHear = ({ 
  text, 
  language = 'en',
  className 
}: TapToHearProps) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if speech synthesis is available
    setIsSupported('speechSynthesis' in window);
  }, []);

  const handleSpeak = useCallback(() => {
    if (!isSupported || !text) return;

    // If already speaking, stop
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.pitch = 1;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  }, [text, language, isSupported, isSpeaking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSupported]);

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  return (
    <button
      onClick={handleSpeak}
      aria-label={isSpeaking ? 'Stop speaking' : 'Read aloud'}
      className={cn(
        'p-2 rounded-full transition-all duration-200',
        'hover:bg-muted active:scale-95',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        isSpeaking && 'bg-primary/10',
        className
      )}
    >
      {isSpeaking ? (
        <Volume2 
          className="w-5 h-5 text-primary animate-pulse" 
        />
      ) : (
        <VolumeX className="w-5 h-5 text-muted-foreground" />
      )}
    </button>
  );
};
